/**
 * As regras que levam as casas do cadastro e os lançamentos para os blocos e
 * para o `<select>` de Residências.
 *
 * Papel: aqui ficam `separarCasas`, que tira a fazenda das casas de topo e
 * ordena as que ganham bloco; `separarPorBloco`, que diz em que bloco cai cada
 * lançamento; `opcoesDeResidencia`, que monta o `<select>` do formulário;
 * `residenciaDoLancamentoNovo`, que dá a casa inicial do lançamento novo; e
 * `rotuloDaCasa` e `subtituloDoBloco`, os dois textos compostos da casa.
 *
 * Como usar: a página e o formulário deverão passar por estas funções, e nunca
 * repetir a comparação com `NOME_DA_FAZENDA` nem a ordenação por conta própria.
 * A fazenda é tirada pelo nome e posta na frente das opções porque a ordem
 * alfabética poria a casa de São Paulo antes dela, e a ordem da origem é
 * fazenda, casa de São Paulo, "Ambas / não se aplica".
 *
 * O lançamento da fazenda não cai em bloco nenhum: a fazenda não tem bloco, tem
 * o `MenuBoaVista`, e o lançamento fica gravado e invisível, como na origem.
 * Renomear a fazenda no cadastro sem renomear `NOME_DA_FAZENDA` a transforma em
 * casa de bloco, e os lançamentos dela passam a aparecer no bloco novo.
 */
import { type CasaDoLancamento, type LancamentoResidencia } from '@concierge/shared';

import { NOME_DA_FAZENDA } from '@/modules/cadastros-base';
import { ROTULO_SEM_RESIDENCIA } from './rotulos';

/** As casas de topo como a tela as usa. */
export interface CasasSeparadas {
  /** A casa de topo cujo `nome` é `NOME_DA_FAZENDA`, ou `null` quando não há. */
  fazenda: CasaDoLancamento | null;
  /** As outras casas de topo, em ordem alfabética, uma por bloco. */
  casasDeBloco: CasaDoLancamento[];
}

/** O bloco de onde a pessoa abriu o lançamento: o de uma casa, ou o de "Ambas". */
export type Bloco = { tipo: 'casa'; casa: CasaDoLancamento } | { tipo: 'ambas' };

/** Os lançamentos repartidos entre os blocos da tela. */
export interface LancamentosPorBloco {
  /** Os de cada casa de bloco, pelo `id` da casa; a casa sem lançamento tem lista vazia. */
  porCasa: Map<string, LancamentoResidencia[]>;
  /** Os de "Ambas / não se aplica". */
  ambas: LancamentoResidencia[];
}

/** Uma opção do `<select>` de residência. */
export interface OpcaoDeResidencia {
  /** O `value` da opção: o `id` da casa, ou `''` para "Ambas / não se aplica". */
  valor: string;
  rotulo: string;
}

/**
 * Separa a fazenda das casas que ganham bloco.
 *
 * Quem chama é a página, com as `casas` de `GET /api/residencias/lancamentos`,
 * que são só as de topo. A ordem de entrada não importa: as casas de bloco saem
 * por `localeCompare('pt-BR')` pelo nome, o que põe "Ágata" antes de "Bosque" —
 * uma ordenação por _code unit_ a jogaria para o fim.
 *
 * @param casas - As casas de topo, em qualquer ordem.
 * @returns A fazenda, ou `null` quando nenhuma casa tem o nome dela, e as
 *   outras em ordem alfabética.
 */
export function separarCasas(casas: readonly CasaDoLancamento[]): CasasSeparadas {
  const fazenda = casas.find((casa) => casa.nome === NOME_DA_FAZENDA) ?? null;
  const casasDeBloco = casas
    .filter((casa) => casa !== fazenda)
    .sort((uma, outra) => uma.nome.localeCompare(outra.nome, 'pt-BR'));

  return { fazenda, casasDeBloco };
}

/**
 * Reparte os lançamentos entre os blocos, como os dois filtros da origem.
 *
 * O lançamento de uma casa de bloco vai para ela; o sem casa **e** o de uma
 * casa que não é a fazenda nem casa de bloco — a que passou a ficar dentro de
 * outra — vão para "Ambas"; o da fazenda não vai para lugar nenhum. A ordem
 * dentro de cada bloco é a de chegada, que a API dá por criação.
 *
 * @param lancamentos - Os lançamentos, na ordem da API.
 * @param casas - O resultado de `separarCasas` sobre as mesmas casas da listagem.
 * @returns Uma lista por casa de bloco, vazia quando a casa não tem lançamento,
 *   e a lista de "Ambas".
 */
export function separarPorBloco(
  lancamentos: readonly LancamentoResidencia[],
  casas: CasasSeparadas,
): LancamentosPorBloco {
  const porCasa = new Map<string, LancamentoResidencia[]>(
    casas.casasDeBloco.map((casa) => [casa.id, []]),
  );
  const ambas: LancamentoResidencia[] = [];

  for (const lancamento of lancamentos) {
    const casaId = lancamento.residencia?.id ?? null;
    if (casaId !== null && casaId === casas.fazenda?.id) continue;

    const doBloco = casaId === null ? undefined : porCasa.get(casaId);
    if (doBloco === undefined) ambas.push(lancamento);
    else doBloco.push(lancamento);
  }

  return { porCasa, ambas };
}

/**
 * O texto da opção de uma casa: `nome (cidade, UF)`.
 *
 * É a composição que reproduz caractere por caractere as duas constantes de
 * casa da origem.
 *
 * @param casa - A casa, com nome, cidade e UF.
 * @returns O texto da `<option>`.
 */
export function rotuloDaCasa(casa: Pick<CasaDoLancamento, 'nome' | 'cidade' | 'uf'>): string {
  return `${casa.nome} (${casa.cidade}, ${casa.uf})`;
}

/**
 * O subtítulo do bloco de uma casa: a cidade em caixa alta e a UF.
 *
 * A caixa alta é do texto, e não de CSS: o `.sub` do cabeçalho de bloco da
 * origem não tem `text-transform`, e um `uppercase` no componente mudaria
 * também o subtítulo do `MenuBoaVista`.
 *
 * @param casa - A casa, com cidade e UF.
 * @returns Como `SÃO PAULO, SP`.
 */
export function subtituloDoBloco(casa: Pick<CasaDoLancamento, 'cidade' | 'uf'>): string {
  return `${casa.cidade.toLocaleUpperCase('pt-BR')}, ${casa.uf}`;
}

/**
 * As opções do `<select>` de residência, na ordem da origem.
 *
 * A fazenda vem primeiro, depois as casas de bloco na ordem dos blocos, e
 * "Ambas / não se aplica" por último, com `valor` vazio. Quem chama é o
 * formulário, que passa a casa do lançamento em edição: quando ela não está
 * entre as de topo, entra como opção a mais, logo antes de "Ambas", para que
 * salvar sem mexer não troque a casa do lançamento sem aviso.
 *
 * @param casas - O resultado de `separarCasas`.
 * @param casaDoLancamento - A casa gravada no lançamento em edição; `null` no
 *   lançamento novo e no lançamento sem casa.
 * @returns As opções, cada uma com `valor` e `rotulo`.
 */
export function opcoesDeResidencia(
  casas: CasasSeparadas,
  casaDoLancamento: CasaDoLancamento | null,
): OpcaoDeResidencia[] {
  const deTopo =
    casas.fazenda === null ? casas.casasDeBloco : [casas.fazenda, ...casas.casasDeBloco];
  const opcoes = deTopo.map((casa) => ({ valor: casa.id, rotulo: rotuloDaCasa(casa) }));

  if (casaDoLancamento !== null && !deTopo.some((casa) => casa.id === casaDoLancamento.id)) {
    opcoes.push({ valor: casaDoLancamento.id, rotulo: rotuloDaCasa(casaDoLancamento) });
  }

  return [...opcoes, { valor: '', rotulo: ROTULO_SEM_RESIDENCIA }];
}

/**
 * A casa com que o lançamento novo nasce: a do bloco de onde ele foi aberto.
 *
 * Quem chama é o formulário, no lançamento novo. No bloco de uma casa, é ela; no
 * bloco "Ambas / não se aplica", é "Ambas". Sem esta regra, o lançamento salvo
 * sem trocar a residência iria para a primeira opção, que é a fazenda, e sumiria
 * da tela.
 *
 * @param bloco - O bloco onde a pessoa clicou em "+ Novo lançamento".
 * @returns O `id` da casa do bloco, ou `null` para "Ambas / não se aplica".
 */
export function residenciaDoLancamentoNovo(bloco: Bloco): string | null {
  return bloco.tipo === 'casa' ? bloco.casa.id : null;
}
